/**
 * Hermes Agent Dashboard
 * ======================
 * Monitors Hermes self-learning activity, optimization jobs, pattern proposals,
 * and AI-generated summaries of what Hermes is doing and how results are trending.
 *
 * Auto-refreshes every 60 seconds. Historical improvement data is tracked across
 * each poll to build a timeline of Sharpe/win-rate changes per pattern.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  ArrowDownRight,
  Bot,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Clock,
  FlaskConical,
  Loader2,
  RefreshCw,
  Sparkles,
  TrendingUp,
  XCircle,
  Zap,
} from 'lucide-react';
import { hermesApi, ApiError } from '../lib/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

// ── Types ────────────────────────────────────────────────────────────────────

interface PatternMetricSnapshot {
  pattern_id: string;
  name: string;
  win_rate: number;
  sharpe: number;
  profit_factor: number;
  total_trades: number;
  needs_optimization: boolean;
  recorded_at: number; // epoch ms
}

interface ImprovementRecord {
  pattern_id: string;
  name: string;
  before_sharpe: number;
  after_sharpe: number;
  before_win_rate: number;
  after_win_rate: number;
  proposal_id: string;
  approved_at: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

const REFRESH_CADENCE_SECONDS = 60;
// Minimum spacing between (auto) AI-summary regenerations — caps LLM token spend.
const SUMMARY_MIN_INTERVAL_MS = 10 * 60 * 1000;

// ── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number) {
  return `${(n * 100).toFixed(1)}%`;
}
function fixed(n: number, d = 2) {
  return n.toFixed(d);
}

function deltaClass(v: number) {
  if (v > 0) return 'text-emerald-500';
  if (v < 0) return 'text-red-500';
  return 'text-muted-foreground';
}

function DeltaBadge({ before, after, format = 'pct' }: { before: number; after: number; format?: 'pct' | 'num' }) {
  const delta = after - before;
  const fmt = format === 'pct' ? pct(delta) : fixed(delta, 2);
  const label = delta > 0 ? `+${fmt}` : fmt;
  if (delta === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className={`text-xs font-semibold flex items-center gap-0.5 ${deltaClass(delta)}`}>
      {delta > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
      {label}
    </span>
  );
}

function StatusBadge({ level }: { level: string }) {
  const key = (level ?? 'UNKNOWN').toUpperCase();
  const map: Record<string, string> = {
    NORMAL:    'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
    CAUTIOUS:  'bg-amber-500/15 text-amber-600 border-amber-500/30',
    HALTED:    'bg-red-500/15 text-red-600 border-red-500/30',
    EMERGENCY: 'bg-red-700/20 text-red-700 border-red-700/30',
    UNKNOWN:   'bg-muted/30 text-muted-foreground border-muted',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${map[key] ?? map.UNKNOWN}`}>
      {key}
    </span>
  );
}

function JobStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    queued:   'bg-blue-500/10 text-blue-600 border-blue-500/30',
    running:  'bg-amber-500/10 text-amber-600 border-amber-500/30',
    complete: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    failed:   'bg-red-500/10 text-red-600 border-red-500/30',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${map[status] ?? 'bg-muted/20 text-muted-foreground border-muted'}`}>
      {status}
    </span>
  );
}

function SectionCard({
  title,
  icon: Icon,
  children,
  action,
  className = '',
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`rounded-xl border bg-card p-5 space-y-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function MetricPill({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-muted/30 border border-border">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-sm font-bold text-foreground">{value}</span>
      {sub && <span className="text-[10px] text-muted-foreground">{sub}</span>}
    </div>
  );
}

// ── AI Summary Fetcher ────────────────────────────────────────────────────────

async function fetchAiSummary(context: any, proposals: any[], activeJobs: number): Promise<string> {
  const patternCount = context?.patterns?.length ?? 0;
  const needsOpt = Object.values(context?.pattern_metrics ?? {}).filter((m: any) => m.needs_optimization).length;
  const openPositions = context?.open_positions?.length ?? 0;
  const cbLevel = context?.market_regime?.circuit_breaker ?? 'UNKNOWN';
  const pendingProposals = proposals.filter((p: any) => p.status === 'pending_review').length;
  const approvedProposals = proposals.filter((p: any) => p.status === 'approved').length;

  const prompt = `
Hermes autonomous agent status as of ${new Date().toLocaleString()}:
- ${patternCount} active trading patterns monitored
- ${needsOpt} patterns flagged for optimization (win rate <45% or profit factor <1.0)
- ${openPositions} positions currently open
- Circuit breaker: ${cbLevel}
- ${pendingProposals} pattern proposals awaiting human review
- ${approvedProposals} proposals approved and promoted to live
- ${activeJobs} optimization jobs running right now

Pattern performance metrics:
${Object.entries(context?.pattern_metrics ?? {}).map(([id, m]: [string, any]) =>
    `  - Pattern ${id}: WR=${pct(m.win_rate)}, Sharpe=${fixed(m.sharpe)}, PF=${fixed(m.profit_factor)}, Trades=${m.total_trades}, NeedsOpt=${m.needs_optimization}`
  ).join('\n')}

Question: Based on this data, write a 3-4 sentence executive summary of what the Hermes agent
is currently doing, how the trading strategies are performing, and what the most important
action items are for the trader. Be specific and use the numbers. Focus on insights, not platitudes.
  `.trim();

  try {
    const data = await hermesApi.chat(prompt);
    return data?.reply ?? 'Unable to generate summary.';
  } catch {
    return 'AI summary unavailable — check API connectivity.';
  }
}

// ── Main Component ───────────────────────────────────────────────────────────

const HermesDashboard: React.FC = () => {
  const [context, setContext] = useState<any>(null);
  const [patterns, setPatterns] = useState<any[]>([]);
  const [proposals, setProposals] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(REFRESH_CADENCE_SECONDS);
  const [aiSummary, setAiSummary] = useState<string>('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [activeJobs, setActiveJobs] = useState<Map<string, any>>(new Map());
  const [expandedPattern, setExpandedPattern] = useState<string | null>(null);
  const [triggeringOpt, setTriggeringOpt] = useState<string | null>(null);
  const [approvingProposal, setApprovingProposal] = useState<string | null>(null);
  const [tab, setTab] = useState('overview');
  // Inline action feedback (replaces alert) + approval confirm dialog (replaces confirm)
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmApproveId, setConfirmApproveId] = useState<string | null>(null);

  // Persisted (server-side) improvement timeline + metric history — survive reloads.
  const [serverImprovements, setServerImprovements] = useState<ImprovementRecord[]>([]);
  const [serverHistory, setServerHistory] = useState<Record<string, PatternMetricSnapshot[]>>({});

  // Historical snapshots for improvement tracking: pattern_id → snapshots[]
  const historyRef = useRef<Map<string, PatternMetricSnapshot[]>>(new Map());
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const refreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jobPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // AI-summary throttle bookkeeping
  const lastSummarySigRef = useRef<string>('');
  const lastSummaryAtRef = useRef<number>(0);

  // ── Data Fetching ──────────────────────────────────────────────────────────

  const fetchAll = useCallback(async () => {
    setError(null);
    try {
      const [ctx, pats, props, improvements, history] = await Promise.all([
        hermesApi.context(),
        hermesApi.patterns(30),
        hermesApi.proposals(),
        hermesApi.improvements().catch(() => ({ improvements: [] })),
        hermesApi.metricHistory().catch(() => ({ snapshots: [] })),
      ]);
      setContext(ctx);
      setPatterns(pats?.patterns ?? []);
      setProposals(props?.proposals ?? []);
      setServerImprovements(improvements?.improvements ?? []);

      // Group persisted snapshots by pattern_id for the history charts.
      const grouped: Record<string, PatternMetricSnapshot[]> = {};
      (history?.snapshots ?? []).forEach((snap: any) => {
        const ms = Object.entries(snap?.metrics ?? {});
        const recordedMs = Date.parse(snap?.recorded_at ?? '') || Date.now();
        ms.forEach(([pid, m]: [string, any]) => {
          (grouped[pid] = grouped[pid] ?? []).push({
            pattern_id: pid,
            name: pid,
            win_rate: m?.win_rate ?? 0,
            sharpe: m?.sharpe ?? 0,
            profit_factor: m?.profit_factor ?? 0,
            total_trades: m?.total_trades ?? 0,
            needs_optimization: false,
            recorded_at: recordedMs,
          });
        });
      });
      setServerHistory(grouped);

      // Record historical snapshot
      const now = Date.now();
      (pats?.patterns ?? []).forEach(({ pattern, metrics }: any) => {
        const id = pattern?.id ?? pattern?.pattern_id;
        if (!id) return;
        const snap: PatternMetricSnapshot = {
          pattern_id: id,
          name: pattern?.name ?? id,
          win_rate: metrics?.win_rate ?? 0,
          sharpe: metrics?.sharpe ?? 0,
          profit_factor: metrics?.profit_factor ?? 0,
          total_trades: metrics?.total_trades ?? 0,
          needs_optimization: metrics?.needs_optimization ?? false,
          recorded_at: now,
        };
        const prev = historyRef.current.get(id) ?? [];
        historyRef.current.set(id, [...prev.slice(-23), snap]); // keep 24 snapshots
      });

      setLastRefresh(new Date());
      setCountdown(REFRESH_CADENCE_SECONDS);
    } catch (e) {
      const msg = e instanceof ApiError ? e.detail : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshAiSummary = useCallback(async (ctx: any, props: any[], jobs: number) => {
    setSummaryLoading(true);
    try {
      const text = await fetchAiSummary(ctx, props, jobs);
      setAiSummary(text);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  // ── Auto-refresh loop ──────────────────────────────────────────────────────

  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    countdownRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          fetchAll();
          return REFRESH_CADENCE_SECONDS;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [fetchAll]);

  // Refresh the AI summary at most once every 10 minutes, and only when the
  // underlying data actually changed — the LLM call costs tokens, so regenerating
  // it on every 60s poll would burn cost for no new information.
  useEffect(() => {
    if (!context || summaryLoading) return;

    // A cheap signature of the inputs that would change the summary.
    const signature = JSON.stringify({
      cb: context?.market_regime?.circuit_breaker,
      pos: context?.open_positions?.length ?? 0,
      metrics: context?.pattern_metrics ?? {},
      pending: proposals.filter((p) => p.status === 'pending_review').length,
      approved: proposals.filter((p) => p.status === 'approved').length,
      jobs: activeJobs.size,
    });

    const now = Date.now();
    const unchanged = signature === lastSummarySigRef.current;
    const tooSoon = now - lastSummaryAtRef.current < SUMMARY_MIN_INTERVAL_MS;
    if (unchanged || tooSoon) return;

    lastSummarySigRef.current = signature;
    lastSummaryAtRef.current = now;
    refreshAiSummary(context, proposals, activeJobs.size);
  }, [context, proposals, activeJobs.size]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll active optimization jobs
  useEffect(() => {
    if (activeJobs.size === 0) {
      if (jobPollRef.current) clearInterval(jobPollRef.current);
      return;
    }
    jobPollRef.current = setInterval(async () => {
      const updated = new Map(activeJobs);
      let anyChanged = false;
      for (const jobId of Array.from(updated.keys())) {
        try {
          const res = await hermesApi.optimizeStatus(jobId);
          updated.set(jobId, res);
          if (res.status === 'complete' || res.status === 'failed') anyChanged = true;
        } catch { /* ignore */ }
      }
      setActiveJobs(updated);
      if (anyChanged) fetchAll();
    }, 10000);
    return () => {
      if (jobPollRef.current) clearInterval(jobPollRef.current);
    };
  }, [activeJobs, fetchAll]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const triggerOptimize = async (patternId: string) => {
    setActionError(null);
    setTriggeringOpt(patternId);
    try {
      const res = await hermesApi.optimize({ pattern_id: patternId, days: 30, strategy: 'balanced' });
      if (res?.job_id) {
        setActiveJobs((prev) => new Map(prev).set(res.job_id, { ...res, pattern_id: patternId }));
      }
    } catch (e) {
      setActionError(`Optimization failed: ${e instanceof ApiError ? e.detail : String(e)}`);
    } finally {
      setTriggeringOpt(null);
    }
  };

  // Two-step approve: request opens the confirm dialog; confirm performs the call.
  const requestApprove = (proposalId: string) => {
    setActionError(null);
    setConfirmApproveId(proposalId);
  };

  const performApprove = async () => {
    const proposalId = confirmApproveId;
    if (!proposalId) return;
    setConfirmApproveId(null);
    setApprovingProposal(proposalId);
    try {
      await hermesApi.approveProposal(proposalId);
      await fetchAll();
    } catch (e) {
      setActionError(`Approval failed: ${e instanceof ApiError ? e.detail : String(e)}`);
    } finally {
      setApprovingProposal(null);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const needsOptCount = patterns.filter((p) => p.metrics?.needs_optimization).length;
  const pendingProposals = proposals.filter((p) => p.status === 'pending_review');
  const approvedProposals = proposals.filter((p) => p.status === 'approved');
  const cbLevel = context?.market_regime?.circuit_breaker ?? 'UNKNOWN';
  const safeToOptimize = context?.safe_to_optimize ?? false;

  // Improvement timeline — prefer the persisted server timeline; fall back to the
  // in-session snapshots if the backend has not accumulated 2+ snapshots yet.
  const improvementTimeline: ImprovementRecord[] = serverImprovements.length
    ? serverImprovements
    : (approvedProposals
        .map((p: any) => {
          const pid = p.promoted_pattern_id ?? '';
          const snapshots = histFor(pid);
          if (snapshots.length < 2) return null;
          const first = snapshots[0];
          const last = snapshots[snapshots.length - 1];
          return {
            pattern_id: pid,
            name: p.pattern?.name ?? pid,
            before_sharpe: first.sharpe,
            after_sharpe: last.sharpe,
            before_win_rate: first.win_rate,
            after_win_rate: last.win_rate,
            proposal_id: p.proposal_id,
            approved_at: p.approved_at ?? '',
          } as ImprovementRecord;
        })
        .filter(Boolean) as ImprovementRecord[]);

  // Snapshot series for a pattern: prefer persisted server history, else live in-session.
  const histFor = (pid: string): PatternMetricSnapshot[] => {
    const server = serverHistory[pid] ?? [];
    if (server.length >= 2) return server;
    return historyRef.current.get(pid) ?? [];
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Bot className="w-5 h-5 text-primary" />
            Hermes Agent Dashboard
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Self-improving strategy monitor — auto-refresh every {REFRESH_CADENCE_SECONDS}s
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            Next refresh in{' '}
            <span className={`font-mono font-semibold ${countdown <= 10 ? 'text-amber-500' : 'text-foreground'}`}>
              {countdown}s
            </span>
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setLoading(true); fetchAll(); }}
            className="gap-1.5"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {actionError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-600 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            {actionError}
          </span>
          <button onClick={() => setActionError(null)} className="text-red-600/70 hover:text-red-600">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Approve-confirmation dialog (replaces window.confirm) */}
      <Dialog open={confirmApproveId !== null} onOpenChange={(open) => !open && setConfirmApproveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promote pattern to live trading?</DialogTitle>
            <DialogDescription>
              This activates the proposed pattern for real trading. It will begin generating
              signals on the next pattern reload. You can deactivate it later from Bot Management.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmApproveId(null)}>
              Cancel
            </Button>
            <Button onClick={performApprove}>
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Promote to live
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {context?.truncated && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-xs text-amber-600 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Metrics computed on a partial sample — the trade-log query hit its cap. Numbers
          reflect the most recent trades only. Narrow the window for exact figures.
        </div>
      )}

      {/* Status bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricPill
          label="Circuit Breaker"
          value={cbLevel}
          sub={safeToOptimize ? 'Safe to optimize' : 'Optimization paused'}
        />
        <MetricPill
          label="Patterns Monitored"
          value={String(patterns.length)}
          sub={`${needsOptCount} need attention`}
        />
        <MetricPill
          label="Pending Proposals"
          value={String(pendingProposals.length)}
          sub="awaiting approval"
        />
        <MetricPill
          label="Active Jobs"
          value={String(activeJobs.size)}
          sub={lastRefresh ? `Updated ${lastRefresh.toLocaleTimeString()}` : 'Not yet loaded'}
        />
      </div>

      {/* AI Summary */}
      <SectionCard title="Hermes Intelligence Brief" icon={Brain}>
        {summaryLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Generating AI summary…
          </div>
        ) : aiSummary ? (
          <p className="text-sm text-foreground leading-relaxed">{aiSummary}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Summary will appear after first data load.</p>
        )}
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs gap-1.5"
            onClick={() => {
              // Manual regenerate bypasses the throttle but updates its bookkeeping.
              lastSummaryAtRef.current = Date.now();
              refreshAiSummary(context, proposals, activeJobs.size);
            }}
            disabled={summaryLoading}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Re-generate
          </Button>
        </div>
      </SectionCard>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Patterns</TabsTrigger>
          <TabsTrigger value="proposals">
            Proposals
            {pendingProposals.length > 0 && (
              <span className="ml-1.5 rounded-full bg-amber-500 text-white text-[10px] px-1.5 py-px font-bold">
                {pendingProposals.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="jobs">Opt Jobs</TabsTrigger>
          <TabsTrigger value="improvements">Improvements</TabsTrigger>
        </TabsList>

        {/* ── Patterns Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-3 mt-4">
          {patterns.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No active patterns. Create bots in Bot Management first.
            </p>
          )}
          {patterns.map(({ pattern, metrics }: any) => {
            const pid = pattern?.id ?? pattern?.pattern_id ?? '';
            const isExpanded = expandedPattern === pid;
            const snapshots = histFor(pid);
            const prev = snapshots.length >= 2 ? snapshots[snapshots.length - 2] : null;

            return (
              <div key={pid} className="rounded-xl border bg-card overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/20 transition-colors"
                  onClick={() => setExpandedPattern(isExpanded ? null : pid)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${metrics?.needs_optimization ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                    <div>
                      <p className="font-semibold text-sm">{pattern?.name ?? pid}</p>
                      <p className="text-xs text-muted-foreground">{pattern?.asset_class ?? 'stocks'} · {metrics?.total_trades ?? 0} trades / 30d</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="hidden sm:flex items-center gap-4 text-sm">
                      <span className="text-muted-foreground text-xs">WR{' '}
                        <span className="font-bold text-foreground">{pct(metrics?.win_rate ?? 0)}</span>
                        {prev && <DeltaBadge before={prev.win_rate} after={metrics?.win_rate ?? 0} />}
                      </span>
                      <span className="text-muted-foreground text-xs">Sharpe{' '}
                        <span className="font-bold text-foreground">{fixed(metrics?.sharpe ?? 0)}</span>
                        {prev && <DeltaBadge before={prev.sharpe} after={metrics?.sharpe ?? 0} format="num" />}
                      </span>
                      <span className="text-muted-foreground text-xs">PF{' '}
                        <span className="font-bold text-foreground">{fixed(metrics?.profit_factor ?? 0)}</span>
                      </span>
                    </div>
                    {metrics?.needs_optimization && (
                      <Badge variant="outline" className="border-amber-500/30 text-amber-600 text-[10px]">
                        Needs review
                      </Badge>
                    )}
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t bg-muted/10 px-5 py-4 space-y-4">
                    {/* Metric pills row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <MetricPill label="Win Rate" value={pct(metrics?.win_rate ?? 0)} />
                      <MetricPill label="Sharpe" value={fixed(metrics?.sharpe ?? 0)} />
                      <MetricPill label="Profit Factor" value={fixed(metrics?.profit_factor ?? 0)} />
                      <MetricPill label="Avg Hold" value={`${fixed(metrics?.avg_hold_minutes ?? 0)}m`} />
                      <MetricPill label="Avg Win" value={`$${fixed(metrics?.avg_win_usd ?? 0)}`} />
                      <MetricPill label="Avg Loss" value={`$${fixed(metrics?.avg_loss_usd ?? 0)}`} />
                      <MetricPill label="Total P&L" value={`$${fixed(metrics?.total_pnl_usd ?? 0)}`} />
                      <MetricPill label="Trades" value={String(metrics?.total_trades ?? 0)} />
                    </div>

                    {/* Loss clusters */}
                    {Object.keys(metrics?.loss_clusters_by_hour ?? {}).length > 0 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Loss clusters by hour (ET):</p>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(metrics.loss_clusters_by_hour)
                            .sort(([, a], [, b]) => (b as number) - (a as number))
                            .map(([hour, count]) => (
                              <span
                                key={hour}
                                className="text-[10px] px-2 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-600 font-mono"
                              >
                                {hour}:00 ET — {String(count)} losses
                              </span>
                            ))}
                        </div>
                      </div>
                    )}

                    {/* History sparkline (text-based) */}
                    {snapshots.length > 1 && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Sharpe history (last {snapshots.length} snapshots):</p>
                        <div className="flex items-end gap-0.5 h-8">
                          {snapshots.map((s, i) => {
                            const max = Math.max(...snapshots.map((x) => x.sharpe), 0.01);
                            const h = Math.max(4, (s.sharpe / max) * 32);
                            return (
                              <div
                                key={i}
                                title={`${new Date(s.recorded_at).toLocaleTimeString()} — Sharpe ${fixed(s.sharpe)}`}
                                className={`rounded-sm flex-1 transition-all ${s.sharpe > 0.5 ? 'bg-emerald-500' : s.sharpe > 0 ? 'bg-amber-500' : 'bg-red-500'}`}
                                style={{ height: `${h}px` }}
                              />
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Action */}
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5 text-xs"
                        disabled={!safeToOptimize || triggeringOpt === pid || (metrics?.total_trades ?? 0) < 20}
                        onClick={() => triggerOptimize(pid)}
                      >
                        {triggeringOpt === pid ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <FlaskConical className="w-3.5 h-3.5" />
                        )}
                        {(metrics?.total_trades ?? 0) < 20
                          ? 'Need ≥20 trades'
                          : !safeToOptimize
                          ? 'Positions open'
                          : 'Run Optimization'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </TabsContent>

        {/* ── Proposals Tab ─────────────────────────────────────────────────── */}
        <TabsContent value="proposals" className="space-y-4 mt-4">
          {proposals.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No proposals yet. Hermes will generate them during nightly review.
            </p>
          )}

          {pendingProposals.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-amber-600 flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4" />
                Pending Review ({pendingProposals.length})
              </h3>
              {pendingProposals.map((p: any) => (
                <div key={p.proposal_id} className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-sm">{p.pattern?.name ?? p.proposal_id}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Proposed {new Date(p.proposed_at).toLocaleString()} · sandbox_only={String(p.sandbox_only)}
                      </p>
                    </div>
                    <JobStatusBadge status={p.status} />
                  </div>
                  <div className="rounded-lg bg-muted/30 border border-border px-3 py-2">
                    <p className="text-xs font-semibold text-muted-foreground mb-0.5">Hermes Rationale</p>
                    <p className="text-sm">{p.rationale}</p>
                    {p.regime_context && (
                      <p className="text-xs text-muted-foreground mt-1">Regime: {p.regime_context}</p>
                    )}
                  </div>
                  {!p.sandbox_only && (
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        variant="default"
                        className="gap-1.5 text-xs"
                        disabled={approvingProposal === p.proposal_id}
                        onClick={() => requestApprove(p.proposal_id)}
                      >
                        {approvingProposal === p.proposal_id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="w-3.5 h-3.5" />
                        )}
                        Approve & Promote
                      </Button>
                    </div>
                  )}
                  {p.sandbox_only && (
                    <p className="text-xs text-muted-foreground italic">
                      Sandbox-only — paper trading for 5 days before promotion is available.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {approvedProposals.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-emerald-600 flex items-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" />
                Approved & Live ({approvedProposals.length})
              </h3>
              {approvedProposals.map((p: any) => (
                <div key={p.proposal_id} className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-sm">{p.pattern?.name ?? p.proposal_id}</p>
                    <p className="text-xs text-muted-foreground">
                      Approved {p.approved_at ? new Date(p.approved_at).toLocaleString() : '—'} ·{' '}
                      Pattern ID: {p.promoted_pattern_id ?? '—'}
                    </p>
                  </div>
                  <JobStatusBadge status="complete" />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* ── Optimization Jobs Tab ─────────────────────────────────────────── */}
        <TabsContent value="jobs" className="space-y-4 mt-4">
          {activeJobs.size === 0 && (
            <div className="text-center py-10 space-y-2">
              <FlaskConical className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No active optimization jobs. Trigger one from the Patterns tab.
              </p>
            </div>
          )}
          {Array.from(activeJobs.values()).map((job: any) => (
            <div key={job.job_id} className="rounded-xl border bg-card p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm font-mono">{job.job_id.slice(0, 8)}…</p>
                  <p className="text-xs text-muted-foreground">Pattern: {job.pattern_id} · Strategy: {job.strategy ?? 'balanced'}</p>
                </div>
                <JobStatusBadge status={job.status} />
              </div>

              {job.status === 'running' && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Running backtests for parameter mutations…
                </div>
              )}

              {job.status === 'complete' && job.results && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {job.results.improvement_found ? (
                      <span className="text-emerald-600 font-semibold text-sm flex items-center gap-1">
                        <TrendingUp className="w-4 h-4" /> Improvement found
                      </span>
                    ) : (
                      <span className="text-muted-foreground text-sm">No meaningful improvement found — current params appear optimal.</span>
                    )}
                  </div>

                  {/* Current baseline */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border bg-muted/20 p-3">
                      <p className="text-xs text-muted-foreground font-semibold mb-2">Current</p>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <span className="text-muted-foreground">Sharpe</span>
                        <span className="font-mono font-semibold">{fixed(job.results.current?.metrics?.sharpe ?? 0)}</span>
                        <span className="text-muted-foreground">Win Rate</span>
                        <span className="font-mono font-semibold">{pct(job.results.current?.metrics?.win_rate ?? 0)}</span>
                        <span className="text-muted-foreground">PF</span>
                        <span className="font-mono font-semibold">{fixed(job.results.current?.metrics?.profit_factor ?? 0)}</span>
                      </div>
                    </div>

                    {job.results.best_variant && (
                      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                        <p className="text-xs text-emerald-600 font-semibold mb-2">Best Variant</p>
                        <div className="grid grid-cols-2 gap-1 text-xs">
                          <span className="text-muted-foreground">Sharpe</span>
                          <span className="font-mono font-semibold flex gap-1">
                            {fixed(job.results.best_variant.metrics?.sharpe ?? 0)}
                            <DeltaBadge
                              before={job.results.current?.metrics?.sharpe ?? 0}
                              after={job.results.best_variant.metrics?.sharpe ?? 0}
                              format="num"
                            />
                          </span>
                          <span className="text-muted-foreground">Win Rate</span>
                          <span className="font-mono font-semibold flex gap-1">
                            {pct(job.results.best_variant.metrics?.win_rate ?? 0)}
                            <DeltaBadge
                              before={job.results.current?.metrics?.win_rate ?? 0}
                              after={job.results.best_variant.metrics?.win_rate ?? 0}
                            />
                          </span>
                          <span className="text-muted-foreground">PF</span>
                          <span className="font-mono font-semibold">{fixed(job.results.best_variant.metrics?.profit_factor ?? 0)}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-2">
                          Mutation: {JSON.stringify(job.results.best_variant.mutation)}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* All variants ranked */}
                  {(job.results.variants ?? []).length > 0 && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground font-semibold">All variants (ranked by Sharpe)</p>
                      <div className="divide-y divide-border rounded-lg border overflow-hidden">
                        {job.results.variants.map((v: any, i: number) => (
                          <div key={i} className={`px-4 py-2 flex items-center justify-between text-xs ${v.passes_threshold ? 'bg-emerald-500/5' : ''}`}>
                            <span className="font-mono text-muted-foreground">#{i + 1}</span>
                            <span className="text-muted-foreground truncate max-w-[180px]">{JSON.stringify(v.mutation)}</span>
                            <span className="font-mono font-semibold">{fixed(v.metrics?.sharpe ?? 0)}</span>
                            <span>{pct(v.metrics?.win_rate ?? 0)}</span>
                            {v.passes_threshold ? (
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                            ) : (
                              <XCircle className="w-3.5 h-3.5 text-muted-foreground" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {job.status === 'failed' && (
                <p className="text-sm text-red-600">{job.error ?? 'Unknown error'}</p>
              )}
            </div>
          ))}
        </TabsContent>

        {/* ── Improvements Tab ──────────────────────────────────────────────── */}
        <TabsContent value="improvements" className="space-y-4 mt-4">
          <SectionCard
            title="Hermes Impact — Strategy Improvements Over Time"
            icon={TrendingUp}
          >
            {improvementTimeline.length === 0 ? (
              <div className="text-center py-6 space-y-1">
                <Activity className="w-7 h-7 mx-auto text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No improvement data yet. Improvements appear here after Hermes proposals are approved and the dashboard has been running for at least 2 refresh cycles.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {improvementTimeline.map((r) => (
                  <div key={r.proposal_id} className="py-3 grid grid-cols-3 gap-3 text-sm items-center">
                    <div>
                      <p className="font-semibold">{r.name}</p>
                      <p className="text-xs text-muted-foreground">{r.approved_at ? new Date(r.approved_at).toLocaleDateString() : '—'}</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground w-16">Win Rate</span>
                        <span className="font-mono">{pct(r.before_win_rate)}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-mono font-semibold">{pct(r.after_win_rate)}</span>
                        <DeltaBadge before={r.before_win_rate} after={r.after_win_rate} />
                      </div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-muted-foreground w-16">Sharpe</span>
                        <span className="font-mono">{fixed(r.before_sharpe)}</span>
                        <span className="text-muted-foreground">→</span>
                        <span className="font-mono font-semibold">{fixed(r.after_sharpe)}</span>
                        <DeltaBadge before={r.before_sharpe} after={r.after_sharpe} format="num" />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-600">
                        Approved
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {/* Per-pattern sparkline summary */}
          {patterns.length > 0 && (
            <SectionCard title="Per-Pattern Metric History" icon={Activity}>
              <p className="text-xs text-muted-foreground -mt-2">
                Bars show Sharpe ratio across the last {REFRESH_CADENCE_SECONDS}s polling snapshots. Green = Sharpe &gt;0.5, amber = positive, red = negative.
              </p>
              <div className="space-y-4">
                {patterns.map(({ pattern }: any) => {
                  const pid = pattern?.id ?? pattern?.pattern_id ?? '';
                  const snapshots = histFor(pid);
                  if (snapshots.length < 2) return null;
                  const max = Math.max(...snapshots.map((s) => s.sharpe), 0.01);
                  return (
                    <div key={pid}>
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold">{pattern?.name ?? pid}</p>
                        <p className="text-xs text-muted-foreground font-mono">
                          Latest Sharpe: {fixed(snapshots[snapshots.length - 1].sharpe)}
                        </p>
                      </div>
                      <div className="flex items-end gap-0.5 h-8">
                        {snapshots.map((s, i) => {
                          const h = Math.max(4, (Math.max(0, s.sharpe) / max) * 32);
                          return (
                            <div
                              key={i}
                              title={`${new Date(s.recorded_at).toLocaleTimeString()} — ${fixed(s.sharpe)}`}
                              className={`rounded-sm flex-1 ${s.sharpe > 0.5 ? 'bg-emerald-500' : s.sharpe > 0 ? 'bg-amber-400' : 'bg-red-500'}`}
                              style={{ height: `${h}px` }}
                            />
                          );
                        })}
                      </div>
                    </div>
                  );
                }).filter(Boolean)}
                {patterns.every(({ pattern }: any) => {
                  const pid = pattern?.id ?? pattern?.pattern_id ?? '';
                  return histFor(pid).length < 2;
                }) && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    Charts populate after 2+ refresh cycles. Leave this page open.
                  </p>
                )}
              </div>
            </SectionCard>
          )}
        </TabsContent>
      </Tabs>

      {/* Last refresh */}
      {lastRefresh && (
        <p className="text-[11px] text-muted-foreground text-right">
          <Clock className="w-3 h-3 inline mr-1" />
          Last data: {lastRefresh.toLocaleTimeString()}
        </p>
      )}
    </div>
  );
};

export default HermesDashboard;
